/* Route-aware favicon.
 *
 * WHY THIS IS JAVASCRIPT AND NOT A FILE IN public/
 * The admin screens are routes of the same single-page app as the public site,
 * so every one of them is served by the same public/index.html and therefore
 * shares one <link rel="icon">. There is no per-route markup to point at a
 * different file. Swapping the link at runtime is the only way to give
 * /admin/* its own icon.
 *
 * WHY IT MATTERS
 * The admin edits live prices. Having a tab that looks exactly like the public
 * site is how you paste a promo code into the wrong window, or demo the site
 * with the price editor open one tab over. A different colour at 16px is the
 * cheapest possible guard against that.
 *
 * The favicon links in public/index.html are all commented out, so before this
 * the whole app had no icon at all and every tab showed the browser's blank
 * page glyph — public and admin equally anonymous.
 *
 * WHY AN SVG DATA URI RATHER THAN .ico FILES
 * No build step, no binary assets to keep in sync, and the colour is a string
 * in this file. It also stays sharp on any DPI. Safari below 15 ignores SVG
 * favicons and falls back to no icon, which is exactly what it shows today, so
 * nothing regresses there.
 */

/* Deliberately high-contrast against each other rather than "on brand": the
   whole job is being distinguishable in a strip of identical tabs, at about 16
   real pixels, often greyed out because the window is in the background. */
const ICONS = {
  // Public site — the brand blue used across the landing pages.
  site: { bg: "#054EBD", fg: "#FFFFFF", label: "JS" },
  // Admin — amber. Reads as "caution, this one writes to production", and is
  // the furthest thing from blue that still survives being shrunk to a smear.
  admin: { bg: "#E8A21A", fg: "#1A1205", label: "JS" },
};

/* A rounded square with the label centred. Bold and simple on purpose — glyph
   detail is wasted at favicon size, the colour block is what the eye picks up. */
function svgFor({ bg, fg, label }) {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="14" fill="${bg}"/>` +
    `<text x="32" y="33" fill="${fg}" font-family="Helvetica,Arial,sans-serif"` +
    ` font-size="30" font-weight="bold" text-anchor="middle"` +
    ` dominant-baseline="central">${label}</text>` +
    `</svg>`;
  // encodeURIComponent, not base64: it keeps the markup readable in devtools
  // and sidesteps the # and % characters that break a raw data URI.
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

/* Which icon a path should get. Exported so it can be asserted directly
   without a DOM — the interesting bit is the matching, not the <link> write. */
export function iconKeyForPath(pathname) {
  // Matches /admin and everything under it, but NOT a path that merely starts
  // with those letters (/administrators would be a public page).
  return /^\/admin(\/|$)/.test(pathname || "") ? "admin" : "site";
}

/* Tab title and indexing, both of which have the same single-document problem
 * as the favicon.
 *
 * ROBOTS IS THE IMPORTANT HALF. Everything under /admin is a route of the
 * public SPA, so it is served with a 200 and real markup to anything that asks
 * — including crawlers. A static <meta name="robots"> in public/index.html
 * cannot express "noindex here, index everywhere else" because there is only
 * one index.html for the whole app; putting noindex there would deindex the
 * marketing site. So it is written per route, and removed again on the way out.
 *
 * This is defence in depth, not the lock: the passphrase gate and the signed
 * token on every callable are what actually guard the data. Robots only stops
 * an admin URL turning up in someone's search results. */
const TITLES = {
  site: "JS Tradventures",
  admin: "⚙ Admin — JS Tradventures",
};

function applyMeta(key) {
  // Title: at narrow tab widths the text is most of what is visible, so the
  // colour swap alone is not enough to tell two tabs apart.
  document.title = TITLES[key];

  let robots = document.querySelector('meta[data-app-robots]');
  if (key === "admin") {
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      robots.setAttribute("data-app-robots", "");
      document.head.appendChild(robots);
    }
    robots.content = "noindex, nofollow, noarchive";
  } else if (robots) {
    // Navigating admin -> public in the same session must not leave the public
    // page carrying noindex.
    robots.remove();
  }
}

let applied = null;

export function applyFavicon(pathname) {
  if (typeof document === "undefined") return;
  const key = iconKeyForPath(pathname);
  // Route changes fire often; rewriting an identical href makes some browsers
  // re-request and flicker the tab icon.
  if (key === applied) return;
  applied = key;

  applyMeta(key);

  // Reuse our own link if it exists, and remove any others so a stale icon
  // cannot win. Browsers pick unpredictably when several are present.
  document
    .querySelectorAll('link[rel~="icon"]:not([data-app-favicon])')
    .forEach((el) => el.remove());

  let link = document.querySelector("link[data-app-favicon]");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg+xml";
    link.setAttribute("data-app-favicon", "");
    document.head.appendChild(link);
  }
  link.href = svgFor(ICONS[key]);
}
